import {
  LandingSection,
  LandingSectionContent,
  LandingSectionHeader,
  LandingSectionTitle,
} from "@/components/landing-page/landing-page-section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { getServerContext } from "@/middleware";
import { action, useSubmission } from "@solidjs/router";
import ThumbsUpIcon from "lucide-solid/icons/thumbs-up";
import {
  type ComponentProps,
  Show,
  createEffect,
  createSignal,
  on,
  splitProps,
} from "solid-js";
import * as v from "valibot";

const EmailValidationSchema = v.pipe(
  v.string("Please enter a valid email"),
  v.trim(),
  v.nonEmpty("Don't forget to enter your email :)"),
  v.email("Please enter a valid email"),
  v.transform((email) => ({
    email,
    id: email.toLowerCase(),
    timestamp: new Date().toISOString(),
  })),
);

const contactUsAction = action(async (email: string) => {
  "use server";

  const serverCtx = getServerContext();
  const { EMAIL_LIST } = serverCtx.cloudflare.env;
  if (!EMAIL_LIST) {
    return new Error("Error saving email. We'll be in touch soon!");
  }

  const ContactUsFormSchema = v.pipeAsync(
    EmailValidationSchema,
    v.checkAsync(
      async (data) => Boolean(await EMAIL_LIST.get(data.id)),
      "That email is already on our list!",
    ),
  );

  const { id, ...body } = await v.parseAsync(ContactUsFormSchema, email);

  await EMAIL_LIST.put(id, JSON.stringify(body));
  console.log(`# added email ${email} to list`);

  return {
    message: "Thanks for reaching out! We'll be in touch soon.",
  };
});

export function ContactUsSection(props: ComponentProps<"section">) {
  const [local, rest] = splitProps(props, ["class"]);
  const [email, setEmail] = createSignal("");

  const contactUsSubmission = useSubmission(contactUsAction);

  createEffect(
    on(
      () => contactUsSubmission.error,
      (res: Error) => {
        if (res) {
          toast.error(res.message, { duration: 5000 });
        }
      },
    ),
  );

  const isSubmitting = () => contactUsSubmission.pending;
  const hasResult = () => contactUsSubmission.result;

  return (
    <LandingSection class={cn("", local.class)} {...rest}>
      <LandingSectionHeader>
        <LandingSectionTitle>Contact Us</LandingSectionTitle>
      </LandingSectionHeader>
      <LandingSectionContent class="mx-auto flex flex-col justify-center bg-geometric text-center max-lg:min-h-[480px] lg:max-w-full lg:items-center">
        <Card class="@container flex w-full grow flex-col justify-center py-6 lg:py-12">
          <Show
            when={!isSubmitting() && !hasResult()}
            fallback={<PostSubmissionMessage class="absolute inset-0" />}
          >
            <CardHeader>
              <CardTitle class="mb-2 font-semibold text-2xl lg:mb-4 lg:text-3xl">
                Have a question?
              </CardTitle>
              <CardDescription class="mx-auto w-full @lg:max-w-2/3 text-pretty break-words text-base">
                Leave your email and we'll reach out, or send an email to{" "}
                <a
                  href="mailto:hello@positivetractionworkshop.com"
                  class="break-words font-semibold underline-offset-2 hover:underline"
                >
                  hello@positivetractionworkshop.com
                </a>
              </CardDescription>
            </CardHeader>
            <form method="post" action={contactUsAction.with(email())}>
              <CardContent class="p-6">
                <div class="flex w-full flex-col justify-center gap-4 md:flex-row">
                  <div class="flex flex-col">
                    <Input
                      name="email"
                      type="email"
                      placeholder="Your email"
                      autocomplete="email"
                      value={email()}
                      onInput={(e) => setEmail(e.currentTarget.value)}
                    />
                    <p class="mt-2 text-muted-foreground text-xs italic">
                      We'll never share your email with anyone else.
                    </p>
                  </div>
                  <Button
                    type="submit"
                    variant="secondary"
                    class="min-w-fit font-semibold"
                    disabled={contactUsSubmission.pending}
                  >
                    Contact us
                  </Button>
                </div>
              </CardContent>
            </form>
          </Show>
        </Card>
      </LandingSectionContent>
    </LandingSection>
  );
}

function PostSubmissionMessage(props: ComponentProps<"div">) {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "absolute inset-0 flex h-full w-full items-center justify-center text-pretty rounded-lg bg-card",
        local.class,
      )}
      {...rest}
    >
      <CardHeader>
        <CardTitle class="mb-2 font-semibold text-2xl">
          Thanks for reaching out!
        </CardTitle>
        <CardDescription class="mx-auto w-full @lg:max-w-2/3 text-pretty break-words text-base">
          We'll get back to you soon. In the meantime, feel free to send us an
          email at
          <a
            href="mailto:hello@positivetractionworkshop.com"
            class="break-words font-semibold underline-offset-2 hover:underline"
          >
            hello@positivetractionworkshop.com
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent class="p-6">
        <div class="flex w-full flex-col justify-center gap-4 md:flex-row">
          <ThumbsUpIcon class="size-18 text-muted-foreground" />
        </div>
      </CardContent>
    </div>
  );
}
